import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Keyboard, Search, FileText, ClipboardList } from 'lucide-react';

export default function KeyboardShortcutsModal({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  const isAdminOrSupervisor = user?.role === 'admin' || user?.role === 'supervisor';

  const shortcuts = [
    {
      key: 'Ctrl+K',
      macKey: '⌘K',
      description: t('shortcuts.search'),
      icon: Search,
      available: true,
    },
    {
      key: 'n',
      macKey: 'n',
      description: t('shortcuts.newWorkRequest'),
      icon: FileText,
      available: true,
    },
    {
      key: 'Shift+N',
      macKey: '⇧N',
      description: t('shortcuts.newWorkOrder'),
      icon: ClipboardList,
      available: isAdminOrSupervisor,
      adminOnly: true,
    },
    {
      key: 'Esc',
      macKey: 'Esc',
      description: t('shortcuts.closeDialog'),
      icon: Keyboard,
      available: true,
    },
  ];

  const visibleShortcuts = shortcuts.filter(s => s.available);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-md"
        data-testid="keyboard-shortcuts-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-brand" />
            {t('shortcuts.title')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2 mt-4">
          {visibleShortcuts.map((shortcut, index) => {
            const Icon = shortcut.icon;
            return (
              <div
                key={index}
                className="flex items-center justify-between py-2.5 px-3 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                data-testid={`shortcut-item-${index}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {shortcut.description}
                  </span>
                  {shortcut.adminOnly && (
                    <span className="text-[10px] uppercase tracking-wider text-brand bg-brand/10 px-1.5 py-0.5 rounded">
                      {t('shortcuts.adminOnly')}
                    </span>
                  )}
                </div>
                <kbd 
                  className={`inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 text-xs font-mono font-medium rounded border border-border bg-background text-muted-foreground shadow-sm ${isRTL ? 'font-sans' : ''}`}
                >
                  {shortcut.key}
                </kbd>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
